import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import classNames from "classnames"
import axios from "axios"
import Lottie from "react-lottie"

// components
import { Icon } from "../Icon/Icon"
import { ProfileMenuBarDropdown } from "./ProfileMenuBarDropdown/ProfileMenuBarDropdown"
import { UpperNavMicroCopy } from "../Header/Header"

// hooks
import { useHandleOutsideClick } from "../../utils/hooks/handle_outside_click.hook"

// redux
import { setUserProfile, logout } from "../../store/slices/user_profile.slice"
import { setMetaData } from "../../store/slices/metadata.slice"

// lottie data
import * as greenLoaderLottieData from "../../icons/lottie/GreenSpinner/Progress loading_circular_Green.json"

// icon(s)
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Typography,
  UserIcon
} from "@eventbrite/marmalade"

// utils
import { events } from "../../utils/heap/tracking_events"
import { enrichUserData } from "../../utils/auth/enrich_user_data"
import { getBaseUrl } from "../../utils/general_utils"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"

// types
import { RootState } from "../../store/store"

// styles
import {
  profileMenuBarContainer,
  profileUserTextContainer,
  profileLoginText,
  active,
  loadingSpinner,
  profileLoginTextHomepage
} from "./ProfileMenuBar.module.scss"

export interface EventBriteEmail {
  email: string
  primary: boolean
  verified: boolean
}

export type EventbriteUserResponse = {
  emails: EventBriteEmail[]
  first_name: string
  id: string
  image_id: string
  is_public: boolean
  last_name: string
  name: string
  redirect_url?: string
  error?: string
}

const validate = async () => {
  if (typeof window !== "undefined") {
    const controller = new AbortController()
    const timeoutController = setTimeout(() => {
      controller.abort()
    }, 20000)

    try {
      const response = await axios.get<EventbriteUserResponse>(
        `${getBaseUrl()}/api/v3/users/me/`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json"
          }
        }
      )

      return {
        data: response.data
      }
    } catch (error) {
      const errorMessage =
        error?.name === "AbortError"
          ? "Request timed out. Please check your connection and try again."
          : error?.message?.length > 0
          ? error?.message
          : "There was an error authorizing request."

      process.env.GATSBY_DEBUG === "true" &&
        console.warn("Login error: ", { error, errorMessage })
    } finally {
      clearTimeout(timeoutController)
    }
  }
}

export const goToLoginPage = (returnUrl = "") => {
  if (typeof window !== "undefined") {
    const returnPath = returnUrl ? returnUrl : window?.location?.pathname

    const finalAuthRedirectUrl = `${getBaseUrl()}/signin/?referrer=${encodeURIComponent(
      returnPath
    )}`

    location.href = finalAuthRedirectUrl
  }
}

export const returnLoginUrl = (returnUrl = "") => {
  if (typeof window !== "undefined") {
    const returnPath = returnUrl ? returnUrl : window?.location?.pathname

    const finalAuthRedirectUrl = `${getBaseUrl()}/signin/?referrer=${encodeURIComponent(
      returnPath
    )}`

    return finalAuthRedirectUrl
  }
}

type Props = {
  microCopy: UpperNavMicroCopy
}

export const ProfileMenuBar: FC<Props> = ({ microCopy }) => {
  const [isAttemptingInitialLogin, setIsAttemptingInitialLogin] =
    useState(false)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)

  const userProfile = useSelector((state: RootState) => state.userProfile)
  const isLoggedIn = useSelector((state: RootState) => state?.userProfile)
    ? true
    : false
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state?.metaData?.hasAttemptedLogin
  )

  const dispatch = useDispatch()
  const ref = useRef<HTMLDivElement>(null)

  const closeDropdown = useCallback(() => {
    setIsProfileMenuOpen(false)
  }, [isProfileMenuOpen])
  const toggleDropdown = useCallback(() => {
    setIsProfileMenuOpen(!isProfileMenuOpen)
  }, [isProfileMenuOpen])

  const handleLogin = useCallback(async () => {
    if (!isAttemptingInitialLogin) {
      if (isLoggedIn) {
        dispatch(logout())

        events?.logoutEvent(ProfileMenuBar?.name)
      } else {
        // logging in
        events?.loginEvent(ProfileMenuBar?.name)

        goToLoginPage()
      }
    }
  }, [isLoggedIn, events, setUserProfile, isAttemptingInitialLogin])

  useHandleOutsideClick({
    callback: closeDropdown,
    ref: ref,
    shouldFire: isProfileMenuOpen
  })

  useEffect(() => {
    ;(async () => {
      if (!hasAttemptedLogin) {
        try {
          setIsAttemptingInitialLogin(true)
          const authResponse = await validate()

          if (authResponse?.data?.id?.length > 0) {
            const enrichedUserData = await enrichUserData(authResponse.data)

            dispatch(setUserProfile(enrichedUserData))
          }
        } catch (error) {
          process.env.GATSBY_DEBUG === "true" && console.warn({ error })
        } finally {
          setIsAttemptingInitialLogin(false)
          dispatch(
            setMetaData({
              hasAttemptedLogin: true
            })
          )
        }
      }
    })()
  }, [])

  if (!hasAttemptedLogin) {
    return (
      <div className={loadingSpinner}>
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData: greenLoaderLottieData,
            rendererSettings: {
              preserveAspectRatio: "xMidYMid slice"
            }
          }}
          width={24}
          height={24}
        />
      </div>
    )
  }

  if (!userProfile) {
    // @TODO: need some attributes to indicate this is for logging in
    return (
      <button
        className={classNames(profileLoginText, profileLoginTextHomepage)}
        style={{
          border: 0,
          backgroundColor: "transparent"
        }}
        onClick={
          !isAttemptingInitialLogin
            ? handleLogin
            : () => {
                return null
              }
        }
      >
        <Typography variant="heading-xs">
          {renderRichText(
            microCopy?.navigationLogIn?.raw,
            customRendererToRemovePTag
          )}
        </Typography>
      </button>
    )
  }

  return (
    <div
      className={classNames(profileMenuBarContainer, {
        [active]: isProfileMenuOpen
      })}
      onClick={toggleDropdown}
      ref={ref}
    >
      <div>
        <Icon
          icon={
            userProfile?.avatar?.url?.length > 0 ? (
              <img src={userProfile?.avatar?.url} alt="User profile picture" />
            ) : (
              <UserIcon size="medium" />
            )
          }
          borderRadius={"50%"}
        />
      </div>
      <div className={profileUserTextContainer}>
        <p>{userProfile?.name}</p>
      </div>
      <div>
        {isProfileMenuOpen ? (
          <Icon icon={<ChevronUpIcon />} />
        ) : (
          <Icon icon={<ChevronDownIcon />} />
        )}
      </div>

      {isProfileMenuOpen ? (
        <ProfileMenuBarDropdown
          isOpen={isProfileMenuOpen}
          userProfile={userProfile}
          handleLogin={handleLogin}
          microCopy={microCopy}
        />
      ) : null}
    </div>
  )
}
